import { FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ITrainControlsResponse } from "../../interfaces";
import { useAlarmColor } from "../../../../../../../../hooks/use-alarm-color";
import { useBlinkAnimation } from "../use-blink-animation";
import { isNeutralAlarmStatusCategory, mapAlarmStatusToCategory } from "../../../../../../../../utils/alarm-colors";
import { resolveDelphiColor } from "../delphi-color";

const H_PAD_PX = 12;
const FIT_WIDTH_FUDGE_PX = 2;

function fitCaptionFontPx(text: string, maxWidth: number, basePx: number, fontFamilyStack: string): number {
  const minPx = 6;
  if (!text.trim() || maxWidth <= 0) return Math.max(minPx, Math.min(basePx, 22));
  if (typeof document === "undefined") return basePx;

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return basePx;

  const measure = (px: number) => {
    ctx.font = `400 ${px}px ${fontFamilyStack}`;
    return ctx.measureText(text).width;
  };

  if (measure(basePx) <= maxWidth) return basePx;

  for (let px = basePx; px >= minPx; px -= 0.5) {
    if (measure(px) <= maxWidth) return px;
  }
  return minPx;
}

interface ITiSchemeButtonProps {
  control: ITrainControlsResponse;
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
}

const DEFAULT_FILL = "rgba(236, 236, 236, 0.92)";

export const TiSchemeButton: FC<ITiSchemeButtonProps> = ({ control, flashUnacknowledgedAlarms, flashFaultAlarms }) => {
  const { value, props, unacknowledgedEvent, stationSchemeStatusIndicator } = control;
  const isStatusFill = stationSchemeStatusIndicator === true;
  const alarmStatusNum = Number(control.alarmStatus);
  const alarmStatusNorm = Number.isFinite(alarmStatusNum) ? alarmStatusNum : 0;
  const statusCategory = mapAlarmStatusToCategory(alarmStatusNorm);
  const statusIndicatorEmpty = isStatusFill && isNeutralAlarmStatusCategory(statusCategory);
  const alarmFillColor = useAlarmColor(alarmStatusNorm);
  const boxRef = useRef<HTMLDivElement>(null);
  const [fitFontPx, setFitFontPx] = useState<number | null>(null);

  const delphiBackgroundColor = useMemo(() => {
    const raw =
      props?.BackGroundColor ??
      (props as { BackgroundColor?: unknown })?.BackgroundColor ??
      (props as { BackColor?: unknown })?.BackColor;
    const resolved = resolveDelphiColor(raw);
    if (resolved) return resolved;
    return DEFAULT_FILL;
  }, [props]);

  const backgroundColor = isStatusFill ? alarmFillColor : delphiBackgroundColor;

  const textColor = useMemo(() => {
    const raw = props?.Font?.Color;
    const resolved = resolveDelphiColor(raw);
    if (resolved) return resolved;
    return "#111111";
  }, [props]);

  const baseFontPx = useMemo(() => {
    const h = props?.Font?.Height;
    if (typeof h === "number" && Number.isFinite(h)) {
      return Math.min(22, Math.max(10, Math.abs(h)));
    }
    return 12;
  }, [props]);

  const fontFamily = useMemo(() => {
    const name = props?.Font?.Name;
    if (typeof name === "string" && name.trim() !== "") {
      const q = name.includes(" ") ? `"${name.trim()}"` : name.trim();
      return `${q}, Tahoma, sans-serif`;
    }
    return "Tahoma, sans-serif";
  }, [props]);

  const captionText = isStatusFill ? "" : value ?? "";

  const captionAlign = useMemo((): "left" | "center" | "right" => {
    const ta = (props as { textAlign?: unknown }).textAlign;
    if (ta === "left" || ta === "center" || ta === "right") return ta;
    const al = (props as { Alignment?: unknown }).Alignment;
    if (typeof al !== "string") return "center";
    const a = al.trim().toLowerCase();
    if (a.includes("right")) return "right";
    if (a.includes("left")) return "left";
    if (a.includes("center")) return "center";
    return "center";
  }, [props]);

  const shouldBlinkBg = flashUnacknowledgedAlarms && unacknowledgedEvent > 0;
  const shouldBlinkBorder = flashFaultAlarms && unacknowledgedEvent === 2;

  const { animation, keyframes } = useBlinkAnimation(
    shouldBlinkBg,
    shouldBlinkBorder,
    backgroundColor,
    isStatusFill ? "rgba(0,0,0,0.35)" : "#2d2d2d",
  );

  const radius = useMemo(() => {
    const w = Number(props.Width) || 0;
    const h = Number(props.Height) || 0;
    const m = Math.min(w, h);
    return `${Math.max(4, Math.min(10, Math.round(m * 0.14)))}px`;
  }, [props.Width, props.Height]);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return;

    const run = () => {
      const inner = Math.max(0, el.clientWidth - H_PAD_PX - FIT_WIDTH_FUDGE_PX);
      setFitFontPx(fitCaptionFontPx(captionText, inner, baseFontPx, fontFamily));
    };

    run();

    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(run);
    ro.observe(el);
    return () => ro.disconnect();
  }, [captionText, baseFontPx, fontFamily]);

  const fontSizePx = fitFontPx ?? baseFontPx;

  if (statusIndicatorEmpty) {
    return null;
  }

  return (
    <Box
      ref={boxRef}
      sx={{
        position: "absolute",
        left: props.Left,
        top: props.Top,
        width: props.Width,
        height: props.Height,
        display: "flex",
        alignItems: "center",
        justifyContent: captionAlign === "left" ? "flex-start" : captionAlign === "right" ? "flex-end" : "center",
        boxSizing: "border-box",
        px: "6px",
        py: "2px",
        borderRadius: radius,
        backgroundColor,
        backgroundImage: isStatusFill
          ? "none"
          : "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.06) 42%, rgba(0,0,0,0.06) 100%)",
        border: isStatusFill ? "1px solid rgba(0,0,0,0.28)" : "1px solid rgba(0,0,0,0.42)",
        boxShadow: isStatusFill
          ? "none"
          : `
          inset 0 1px 0 rgba(255,255,255,0.55),
          inset 0 -1px 0 rgba(0,0,0,0.18),
          0 2px 4px rgba(0,0,0,0.22),
          0 1px 1px rgba(0,0,0,0.12)
        `,
        overflow: "hidden",
        animation,
        ...keyframes,
      }}
    >
      {!isStatusFill ? (
        <Typography
          component="span"
          sx={{
            color: textColor,
            fontSize: `${fontSizePx}px`,
            fontFamily,
            fontWeight: 400,
            textAlign: captionAlign,
            lineHeight: 1.15,
            whiteSpace: "nowrap",
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            userSelect: "none",
            textShadow: "0 1px 0 rgba(255,255,255,0.35)",
            letterSpacing: "0.02em",
          }}
        >
          {captionText}
        </Typography>
      ) : null}
    </Box>
  );
};
